/**
 * @description Two-panel modal for composing and sending payment receipt emails
 *
 * Key features:
 * - Left panel: email controls (to, cc, bcc, subject, body, send-me-a-copy)
 * - Right panel: live receipt preview with payment details (amount, method, date, reference)
 * - Pre-filled from company receipt template (via outreach templates API)
 * - Template variable interpolation with real invoice + payment data
 * - Headless UI Dialog with enterprise modal pattern (unsaved changes confirm)
 * - Responsive: stacked layout on mobile, side-by-side on lg+
 * - CC/BCC email format validation before send
 * - "Send me a copy" pre-checked from company setting email_sender.send_copy_default
 *
 * @dependencies
 * - @headlessui/react: Dialog + Transition
 * - outreachService: fetch company receipt template defaults
 * - settingsService: fetch email_sender.send_copy_default
 * - invoiceService: send receipt email
 * - useCompany: merchant display name for preview
 *
 * @notes
 * - From address is read-only (resolved server-side for security)
 * - Only available when invoice paymentStatus is 'paid' or 'partially_paid'
 * - Payment data resolved from invoice.payments[0] (latest payment, sorted DESC)
 * - Green receipt theme (vs blue Send Invoice theme)
 * - "View Invoice" button in preview (vs "Pay Now" in SendInvoiceModal)
 * - Follows enterprise modal pattern from .claude/context/patterns.md
 */

import React, { useState, useEffect, Fragment, useCallback, useMemo, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, Send, Copy, Eye, EyeOff, Receipt, CheckCircle2 } from 'lucide-react';
import outreachService from '../../services/api/outreachService';
import settingsService from '../../services/api/settingsService';
import invoiceService from '../../services/api/invoiceService';
import { useCompany } from '../../contexts/CompanyContext';
import { useBranding } from '../../contexts/BrandingContext';
import { formatCurrency } from '../../utils/invoiceHelpers';
import { resolveComposeFromEmail } from '../../utils/composeFromEmail';

// Simple email format validation
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const validateEmailList = (emailStr) => {
  if (!emailStr || !emailStr.trim()) return { valid: true, invalid: [] };
  const emails = emailStr.split(',').map(e => e.trim()).filter(Boolean);
  const invalid = emails.filter(e => !isValidEmail(e));
  return { valid: invalid.length === 0, invalid };
};

// Get latest payment from invoice payments array (already sorted DESC by backend)
const getLatestPayment = (invoice) => {
  if (!invoice?.payments || invoice.payments.length === 0) return null;
  return invoice.payments[0];
};

// Build human-readable payment method display string
const getPaymentMethodDisplay = (payment) => {
  if (!payment) return 'Card';
  if (payment.brand && payment.last4) {
    const brand = payment.brand.charAt(0).toUpperCase() + payment.brand.slice(1).toLowerCase();
    return `${brand} ****${payment.last4}`;
  }
  const labels = {
    card: 'Credit Card', credit_card: 'Credit Card',
    ach: 'ACH Bank Transfer', check: 'Check', cash: 'Cash',
    wire: 'Bank Transfer', bank_transfer: 'Bank Transfer',
    external: 'Other', other: 'Other'
  };
  return labels[payment.paymentMethod || payment.cardType] || payment.paymentMethod || 'Card';
};

// Template token → real value mapping for receipt context
const buildTokenMap = (invoice, company, payment) => ({
  '{merchantName}': company?.businessName || 'Your Business',
  '{invoiceNumber}': invoice?.invoiceNumber || '',
  '{customerName}': invoice?.customerName || invoice?.customer?.name || 'Customer',
  '{paymentAmount}': formatCurrency(payment?.amount || invoice?.amountPaid || 0),
  '{paymentMethod}': getPaymentMethodDisplay(payment),
  '{paymentDate}': payment?.paymentDate
    ? new Date(payment.paymentDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
});

const interpolate = (text, tokenMap) => {
  if (!text) return '';
  let result = text;
  Object.entries(tokenMap).forEach(([token, value]) => {
    result = result.replace(new RegExp(token.replace(/[{}]/g, '\\$&'), 'g'), value);
  });
  return result;
};

const SendReceiptModal = ({ isOpen, onClose, invoice, onSuccess }) => {
  const { company } = useCompany();
  const { branding, occupancy } = useBranding();

  // All hooks called first, same order every render (patterns.md compliance)
  const [toEmail, setToEmail] = useState('');
  const [ccEmails, setCcEmails] = useState('');
  const [bccEmails, setBccEmails] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sendCopy, setSendCopy] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  // Initial values for dirty tracking
  const [initialSubject, setInitialSubject] = useState('');
  const [initialBody, setInitialBody] = useState('');
  const [fromEmail, setFromEmail] = useState(
    () => resolveComposeFromEmail({ occupancy, company })
  );

  const cancelCloseRef = useRef(null);
  const prevOpenRef = useRef(false);
  const sendResultRef = useRef(null);

  // Dirty tracking (enterprise pattern)
  const hasUnsavedChanges = useMemo(() => {
    return subject !== initialSubject || body !== initialBody || ccEmails.trim() !== '' || bccEmails.trim() !== '';
  }, [subject, body, ccEmails, bccEmails, initialSubject, initialBody]);

  // Payment data for preview
  const payment = useMemo(() => getLatestPayment(invoice), [invoice]);

  // Token map for preview interpolation
  const tokenMap = useMemo(() => buildTokenMap(invoice, company, payment), [invoice, company, payment]);

  // From address (read-only, resolved server-side)
  const fromDisplay = `"${company?.businessName || 'Your Business'}" <${fromEmail}>`;

  // ---------- Load template defaults + send copy preference ----------

  const loadTemplate = useCallback(async () => {
    try {
      setTemplateLoading(true);

      // Fetch receipt template and send_copy_default in parallel
      const [templateResult, sendCopyResult, logoResult] = await Promise.allSettled([
        outreachService.getTemplate('receipt'),
        settingsService.getSetting('email_sender.send_copy_default'),
        settingsService.getSetting('email_sender.logo_url')
      ]);

      // Receipt template
      if (templateResult.status === 'fulfilled') {
        const val = templateResult.value.currentValue || templateResult.value.defaultValue || {};
        const subj = val.subject || 'Payment Receipt from {merchantName}';
        const bod = val.body || 'Thank you for your payment. Here is your receipt for your records.';
        setSubject(subj);
        setBody(bod);
        setInitialSubject(subj);
        setInitialBody(bod);
        if (templateResult.value.fromEmail) setFromEmail(templateResult.value.fromEmail);
      } else {
        const subj = 'Payment Receipt from {merchantName}';
        const bod = 'Thank you for your payment. Here is your receipt for your records.';
        setSubject(subj);
        setBody(bod);
        setInitialSubject(subj);
        setInitialBody(bod);
      }

      // Pre-check send copy from company setting
      if (sendCopyResult.status === 'fulfilled' && sendCopyResult.value?.value) {
        setSendCopy(Boolean(sendCopyResult.value.value));
      }

      // Email logo
      if (logoResult.status === 'fulfilled' && logoResult.value?.value) {
        setLogoUrl(logoResult.value.value);
      }
    } catch {
      const subj = 'Payment Receipt from {merchantName}';
      const bod = 'Thank you for your payment. Here is your receipt for your records.';
      setSubject(subj);
      setBody(bod);
      setInitialSubject(subj);
      setInitialBody(bod);
    } finally {
      setTemplateLoading(false);
    }
  }, []);

  // Initialize only on open transition (false → true)
  useEffect(() => {
    const justOpened = isOpen && !prevOpenRef.current;
    prevOpenRef.current = isOpen;

    if (justOpened && invoice) {
      sendResultRef.current = null;
      setToEmail(invoice.customerEmail || invoice.customer?.email || '');
      setCcEmails('');
      setBccEmails('');
      setError('');
      setSuccess(false);
      setSending(false);
      setShowPreview(false);
      setShowConfirmClose(false);
      setFromEmail(resolveComposeFromEmail({ occupancy, company }));
      loadTemplate();
    }
  }, [isOpen, invoice, loadTemplate, occupancy, company]);

  // ---------- Auto-close on success ----------

  const closeWithResult = useCallback(() => {
    if (onSuccess && sendResultRef.current) {
      onSuccess(sendResultRef.current);
    }
    onClose();
  }, [onSuccess, onClose]);

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(closeWithResult, 2000);
    return () => clearTimeout(timer);
  }, [success, closeWithResult]);

  // ---------- Close handlers (enterprise pattern) ----------

  const handleClose = () => {
    if (success) {
      closeWithResult();
      return;
    }
    if (hasUnsavedChanges) {
      setShowConfirmClose(true);
    } else {
      onClose();
    }
  };

  const handleForceClose = () => {
    setShowConfirmClose(false);
    onClose();
  };

  // ---------- Send handler ----------

  const handleSend = async () => {
    if (!toEmail.trim()) {
      setError('Recipient email is required');
      return;
    }

    if (!isValidEmail(toEmail)) {
      setError('Please enter a valid recipient email address');
      return;
    }

    // Validate CC emails
    const ccValidation = validateEmailList(ccEmails);
    if (!ccValidation.valid) {
      setError(`Invalid CC email${ccValidation.invalid.length > 1 ? 's' : ''}: ${ccValidation.invalid.join(', ')}`);
      return;
    }

    // Validate BCC emails
    const bccValidation = validateEmailList(bccEmails);
    if (!bccValidation.valid) {
      setError(`Invalid BCC email${bccValidation.invalid.length > 1 ? 's' : ''}: ${bccValidation.invalid.join(', ')}`);
      return;
    }

    try {
      setSending(true);
      setError('');

      const payload = {
        toEmail: toEmail.trim(),
        ...(ccEmails.trim() && { ccEmails: ccEmails.split(',').map(e => e.trim()).filter(Boolean) }),
        ...(bccEmails.trim() && { bccEmails: bccEmails.split(',').map(e => e.trim()).filter(Boolean) }),
        subject,
        body,
        sendCopy
      };

      const result = await invoiceService.sendReceipt(invoice.invoiceNumber, payload);

      sendResultRef.current = result;
      setSending(false);
      setSuccess(true);
    } catch (err) {
      console.error('Error sending receipt:', err);
      setError(err.response?.data?.error || 'Failed to send receipt email');
      setSending(false);
    }
  };

  // Early returns AFTER all hooks (patterns.md compliance)
  if (!invoice) return null;

  return (
    <>
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleClose}>
          {/* Backdrop with blur */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className={`relative transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all duration-500 w-full max-h-[90vh] flex flex-col ${success ? 'max-w-sm' : 'max-w-4xl'}`}>
                  {/* Header — green receipt theme */}
                  <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Receipt className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <Dialog.Title as="h2" className="text-lg font-semibold text-gray-900">
                          Send Receipt
                        </Dialog.Title>
                        <p className="text-sm text-gray-500">
                          {invoice.invoiceNumber} to {invoice.customerName || invoice.customer?.name || 'customer'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleClose}
                      aria-label="Close send receipt modal"
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Scrollable Content */}
                  <div className="flex-1 overflow-y-auto">
                    {/* Success state */}
                    {success ? (
                      <div className="flex items-center justify-center py-20">
                        <div className="text-center">
                          <div className="mx-auto w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-4">
                            <CheckCircle2 className="w-8 h-8 text-green-600" />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">Receipt Sent</h3>
                          <p className="text-sm text-gray-500">
                            Payment receipt for {invoice.invoiceNumber} sent to {toEmail}
                          </p>
                        </div>
                      </div>
                    ) : templateLoading ? (
                      <div className="flex items-center justify-center py-16">
                        <div className="text-center">
                          <div className="animate-spin w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full mx-auto mb-3" />
                          <p className="text-sm text-gray-500 font-medium">Loading template...</p>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
                        {/* ====== Left Panel — Email Controls ====== */}
                        <div className="p-6 space-y-4">
                          {/* Error */}
                          {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                              <p className="text-sm text-red-700">{error}</p>
                            </div>
                          )}

                          {/* From (read-only) */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                              {fromDisplay}
                            </div>
                          </div>

                          {/* To */}
                          <div>
                            <label htmlFor="receipt-to-email" className="block text-sm font-medium text-gray-700 mb-1">
                              To <span className="text-red-500">*</span>
                            </label>
                            <input
                              id="receipt-to-email"
                              type="email"
                              value={toEmail}
                              onChange={(e) => setToEmail(e.target.value)}
                              placeholder="customer@example.com"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            />
                          </div>

                          {/* CC */}
                          <div>
                            <label htmlFor="receipt-cc-emails" className="block text-sm font-medium text-gray-700 mb-1">CC</label>
                            <input
                              id="receipt-cc-emails"
                              type="text"
                              value={ccEmails}
                              onChange={(e) => setCcEmails(e.target.value)}
                              placeholder="cc1@example.com, cc2@example.com"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            />
                            <p className="text-xs text-gray-400 mt-1">Separate multiple addresses with commas</p>
                          </div>

                          {/* BCC */}
                          <div>
                            <label htmlFor="receipt-bcc-emails" className="block text-sm font-medium text-gray-700 mb-1">BCC</label>
                            <input
                              id="receipt-bcc-emails"
                              type="text"
                              value={bccEmails}
                              onChange={(e) => setBccEmails(e.target.value)}
                              placeholder="bcc@example.com"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            />
                          </div>

                          {/* Subject */}
                          <div>
                            <label htmlFor="receipt-subject" className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                            <input
                              id="receipt-subject"
                              type="text"
                              value={subject}
                              onChange={(e) => setSubject(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            />
                          </div>

                          {/* Body */}
                          <div>
                            <label htmlFor="receipt-body" className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                            <textarea
                              id="receipt-body"
                              value={body}
                              onChange={(e) => setBody(e.target.value)}
                              rows={4}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-y"
                            />
                          </div>

                          {/* Send me a copy */}
                          <label htmlFor="receipt-copy-toggle" className="flex items-center gap-2 cursor-pointer">
                            <input
                              id="receipt-copy-toggle"
                              type="checkbox"
                              checked={sendCopy}
                              onChange={(e) => setSendCopy(e.target.checked)}
                              className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                            />
                            <div className="flex items-center gap-1.5">
                              <Copy className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-700">Send me a copy</span>
                            </div>
                          </label>

                          {/* Mobile preview toggle */}
                          <button
                            type="button"
                            onClick={() => setShowPreview(prev => !prev)}
                            className="lg:hidden w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
                          >
                            {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            {showPreview ? 'Hide Preview' : 'Show Preview'}
                          </button>
                        </div>

                        {/* ====== Right Panel — Receipt Preview ====== */}
                        <div className={`${showPreview ? 'block' : 'hidden'} lg:block bg-gray-100 p-6`}>
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Receipt Preview</h4>

                          <div className="max-w-sm mx-auto bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                            {/* Header */}
                            <div className="px-5 pt-5 pb-3 text-center">
                              {logoUrl ? (
                                <img src={logoUrl} alt={company?.businessName || 'Logo'} className="max-h-12 mx-auto mb-2" />
                              ) : (
                                <h2 className="text-base font-bold text-gray-900">
                                  {company?.businessName || 'Your Business'}
                                </h2>
                              )}
                            </div>

                            {/* Subject preview */}
                            <div className="px-5 pb-2">
                              <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Subject</p>
                              <p className="text-sm font-semibold text-gray-900">
                                {interpolate(subject, tokenMap) || 'Payment Receipt from Your Business'}
                              </p>
                            </div>

                            {/* Body preview */}
                            <div className="px-5 py-3">
                              <p className="text-sm font-medium text-gray-900 mb-1.5">
                                Hi {tokenMap['{customerName}']},
                              </p>
                              <p className="text-sm text-gray-600 whitespace-pre-wrap leading-relaxed">
                                {interpolate(body, tokenMap) || 'Your message will appear here.'}
                              </p>
                            </div>

                            {/* Payment details table */}
                            <div className="px-5 py-3 border-t border-gray-100">
                              <div className="space-y-2">
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-500">Amount Paid</span>
                                  <span className="font-bold text-green-600">
                                    {tokenMap['{paymentAmount}']}
                                  </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-500">Payment Method</span>
                                  <span className="font-medium text-gray-900">
                                    {tokenMap['{paymentMethod}']}
                                  </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-500">Date</span>
                                  <span className="font-medium text-gray-900">
                                    {tokenMap['{paymentDate}']}
                                  </span>
                                </div>
                                {payment?.transactionReference && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-500">Reference</span>
                                    <span className="font-medium text-gray-900 font-mono text-xs">
                                      {payment.transactionReference}
                                    </span>
                                  </div>
                                )}
                                <div className="flex justify-between text-sm pt-1 border-t border-gray-100">
                                  <span className="text-gray-500">Invoice</span>
                                  <span className="font-medium text-gray-900">
                                    {invoice.invoiceNumber}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* View Invoice button */}
                            <div className="px-5 py-4 text-center">
                              <div
                                className="inline-block px-6 py-2 text-white text-sm font-semibold rounded-md"
                                style={{ backgroundColor: branding.colors?.primaryColor || '#18181b' }}
                              >
                                View Invoice
                              </div>
                            </div>

                            {/* Footer */}
                            <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 text-center">
                              <p className="text-xs text-gray-500 font-medium">{company?.businessName || 'Your Business'}</p>
                              {company?.addressLine1 && (
                                <p className="text-xs text-gray-400 mt-0.5">
                                  {company.addressLine1}{company.addressLine2 ? `, ${company.addressLine2}` : ''}{company.city ? `, ${company.city}` : ''}{company.state ? `, ${company.state}` : ''} {company.postalCode || ''}
                                </p>
                              )}
                              <p className="text-xs text-gray-400 mt-1">Powered by {branding.identity?.platformName || 'Lynx'}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Sticky Footer — Actions (hidden during success) */}
                  {!templateLoading && !success && (
                    <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
                      <button
                        type="button"
                        onClick={handleClose}
                        disabled={sending}
                        className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSend}
                        disabled={sending || !toEmail.trim()}
                        className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {sending ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            Send Receipt
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Unsaved Changes Confirm Dialog */}
      <Transition.Root show={showConfirmClose} as={Fragment}>
        <Dialog as="div" className="relative z-[60]" onClose={() => setShowConfirmClose(false)} initialFocus={cancelCloseRef}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-[70] overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white px-6 py-5 shadow-2xl transition-all w-full max-w-sm">
                  <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                    Discard Email?
                  </Dialog.Title>
                  <p className="mt-2 text-sm text-gray-500">
                    You've made changes to this receipt email. Are you sure you want to close without sending?
                  </p>
                  <div className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                    <button
                      ref={cancelCloseRef}
                      onClick={() => setShowConfirmClose(false)}
                      className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Keep Editing
                    </button>
                    <button
                      onClick={handleForceClose}
                      className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-500 transition-colors"
                    >
                      Discard
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default SendReceiptModal;
